import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Warehouse, Plus, Trash2, AlertTriangle, Clock, DollarSign, Sparkles, Trash } from "lucide-react";
import api, { apiErr, startAndPollMenu } from "@/lib/api";
import { PageHeader, Loader, StatCard, Badge } from "@/components/kit";
import GenerationOverlay from "@/components/GenerationOverlay";

const CATS = ["Protein", "Produce", "Dairy", "Pantry", "Bar", "General"];
const UNITS = ["lb", "oz", "case", "each", "bottle", "bag", "gal", "qt"];

export default function MyKitchen() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Protein", quantity: 0, unit: "lb", purchase_cost: 0, supplier: "Sysco (Demo)", expiration_date: "", location: "Main", par_level: 0 });
  const [gen, setGen] = useState(false); const [doneMenu, setDoneMenu] = useState(null);

  const load = () => {
    api.get("/inventory").then(({ data }) => setItems(data.inventory || [])).catch(() => setItems([]));
    api.get("/inventory/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name) return toast.error("Name is required");
    try { await api.post("/inventory", form); toast.success("Item added."); setShowAdd(false); setForm({ ...form, name: "", quantity: 0, purchase_cost: 0 }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/inventory/${id}`); load(); };
  const toMenu = async () => {
    setGen(true); setDoneMenu(null);
    try {
      const menu = await startAndPollMenu(
        { mode: "kitchen", courses: 3, guests: 4, prompt: "Prioritize ingredients on hand to reduce waste." },
        { fromInventory: true });
      setDoneMenu(menu);
    } catch (e) { setGen(false); toast.error(apiErr(e) || e.message); }
  };

  if (!items) return <Loader />;
  return (
    <div>
      <PageHeader eyebrow="Inventory Intelligence" title="My Kitchen" subtitle="Track what you have. Chef Plated turns it into margin."
        action={<div className="flex gap-2">
          <button onClick={toMenu} data-testid="inventory-to-menu-btn" className="btn-gold px-5 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><Sparkles size={14} /> Turn Into a Menu</button>
          <button onClick={() => setShowAdd(true)} data-testid="add-inventory-btn" className="btn-outline-gold px-5 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><Plus size={14} /> Add Item</button>
        </div>} />

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Inventory Value" value={`$${summary.inventory_value.toLocaleString()}`} accent icon={DollarSign} />
          <StatCard label="Low Stock" value={summary.low_stock.length} sub="below par" icon={AlertTriangle} />
          <StatCard label="Expiring Soon" value={summary.expiring_soon.length} sub="≤ 5 days" icon={Clock} />
          <StatCard label="Waste Risk" value={`$${summary.waste_risk.toLocaleString()}`} icon={Trash} />
        </div>
      )}

      {summary?.recommendations?.length > 0 && (
        <div className="matte-card p-5 mb-6 gold-border">
          <p className="label-eyebrow mb-3 flex items-center gap-2"><Sparkles size={14} className="text-gold" /> AI Recommendations</p>
          <div className="space-y-2">{summary.recommendations.map((r, i) => <p key={i} className="text-white/70 text-sm">· {r}</p>)}</div>
        </div>
      )}

      {showAdd && (
        <div className="matte-card p-6 mb-6">
          <div className="grid sm:grid-cols-3 gap-3">
            <input placeholder="Ingredient name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
            <div className="flex gap-2">
              <input type="number" placeholder="Qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: +e.target.value })} className="w-1/2 bg-surface border border-white/10 rounded-sm px-3 py-3 text-white/90 outline-none focus:border-gold/60" />
              <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-1/2 bg-surface border border-white/10 rounded-sm px-2 py-3 text-white/90 outline-none focus:border-gold/60">{UNITS.map((u) => <option key={u}>{u}</option>)}</select>
            </div>
            <input type="number" placeholder="Purchase cost $" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: +e.target.value })} className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
            <input type="number" placeholder="Par level" value={form.par_level} onChange={(e) => setForm({ ...form, par_level: +e.target.value })} className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
            <input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/60 outline-none focus:border-gold/60" />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={add} data-testid="save-inventory-btn" className="btn-gold px-6 py-2.5 text-xs uppercase tracking-widest rounded-sm">Save</button>
            <button onClick={() => setShowAdd(false)} className="px-6 py-2.5 text-sm text-white/50 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      <div className="matte-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left label-eyebrow border-b border-white/10">
            <th className="py-4 px-5">Ingredient</th><th className="py-4 px-3">Category</th><th className="py-4 px-3 text-right">Qty</th>
            <th className="py-4 px-3">Location</th><th className="py-4 px-3">Expires</th><th className="py-4 px-3 text-right">Cost</th><th className="py-4 px-5"></th>
          </tr></thead>
          <tbody>
            {items.map((it) => {
              const low = it.par_level && it.quantity < it.par_level;
              return (
                <tr key={it.id} className="border-b border-white/5 hover:bg-black/40">
                  <td className="py-3.5 px-5 text-white/90">{it.name} {low && <Badge tone="red">Low</Badge>}</td>
                  <td className="py-3.5 px-3 text-white/50">{it.category}</td>
                  <td className="py-3.5 px-3 text-right font-mono text-white/70">{it.quantity} {it.unit}</td>
                  <td className="py-3.5 px-3 text-white/50">{it.location}</td>
                  <td className="py-3.5 px-3 text-white/50">{it.expiration_date ? new Date(it.expiration_date).toLocaleDateString() : "—"}</td>
                  <td className="py-3.5 px-3 text-right font-mono text-white/70">${it.purchase_cost}</td>
                  <td className="py-3.5 px-5 text-right"><button onClick={() => del(it.id)} className="text-white/30 hover:text-red-400"><Trash2 size={15} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && <p className="py-12 text-center text-white/40">No inventory yet. Add items or load demo data.</p>}
      </div>

      <GenerationOverlay active={gen} done={!!doneMenu} menuName={doneMenu?.menu_name} onReveal={() => nav(`/menus/${doneMenu.id}`)} />
    </div>
  );
}
